import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Video, MessageCircle, FileText, Bell, X, Clock,
  ArrowRight, Layers, CreditCard, BarChart3, MessageSquare, Reply,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../utils/auth';
import { trackNotification, remindMeLater } from '../../utils/notificationManager';
import './InAppNotification.scss';

// ── Icon map ──
const ICON_MAP = {
  class_started: Video,
  class_ended: Video,
  chat_message: MessageCircle,
  whatsapp_message: MessageSquare,
  assignment_posted: FileText,
  batch_update: Layers,
  payment_received: CreditCard,
  payment_settled: CreditCard,
  ai_report: BarChart3,
  general: Bell,
};

// ── CTA map ──
const CTA_MAP = {
  class_started: { label: 'Join Now', icon: Video },
  chat_message: { label: 'Open Chat', icon: MessageCircle },
  whatsapp_message: { label: 'Open Inbox', icon: MessageSquare },
  assignment_posted: { label: 'View Assignment', icon: ArrowRight },
  batch_update: { label: 'View Details', icon: ArrowRight },
  payment_received: { label: 'View Receipt', icon: ArrowRight },
  ai_report: { label: 'View Report', icon: BarChart3 },
};

// ── Display mode for each type ──
const DISPLAY_MODE = {
  class_started: 'banner',
  class_ended: 'toast',
  chat_message: 'toast',
  whatsapp_message: 'toast',
  assignment_posted: 'toast',
  batch_update: 'toast',
  payment_received: 'toast',
  ai_report: 'toast',
  general: 'toast',
};

const TOAST_DURATION = 8000;
const BANNER_DURATION = 15000;
const MAX_TOASTS = 3;

function getRoutePrefix() {
  const user = getCurrentUser();
  if (user?.usertype === 'student') return '/s';
  if (user?.usertype === 'instructor') return '/i';
  return '';
}

let idCounter = 0;

const InAppNotification = () => {
  const [toasts, setToasts] = useState([]);
  const [banner, setBanner] = useState(null);
  const [modal, setModal] = useState(null);
  const timersRef = useRef(new Map());
  const navigate = useNavigate();

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissNotification = useCallback((id) => {
    clearTimer(id);
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    setBanner((prev) => (prev?.id === id ? { ...prev, exiting: true } : prev));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setBanner((prev) => (prev?.id === id ? null : prev));
    }, 300);
    trackNotification(id, 'dismissed');
  }, [clearTimer]);

  // ── Auto-dismiss timers ──
  const startTimer = useCallback((id, duration) => {
    const timer = setTimeout(() => {
      dismissNotification(id);
    }, duration);
    timersRef.current.set(id, timer);
  }, [dismissNotification]);

  // ── Add notification ──
  const addNotification = useCallback((notification) => {
    const id = `inapp-${++idCounter}`;
    const type = notification.type || 'general';
    const mode = notification.displayMode || DISPLAY_MODE[type] || 'toast';
    const item = {
      ...notification,
      id,
      type,
      mode,
      exiting: false,
      createdAt: Date.now(),
    };

    if (mode === 'modal') {
      setModal(item);
    } else if (mode === 'banner') {
      setBanner((prev) => {
        if (prev) clearTimer(prev.id);
        return item;
      });
      startTimer(id, BANNER_DURATION);
    } else {
      setToasts((prev) => {
        const updated = [...prev, item];
        return updated.length > MAX_TOASTS ? updated.slice(-MAX_TOASTS) : updated;
      });
      startTimer(id, TOAST_DURATION);
    }

    trackNotification(id, 'displayed', { type, mode });
    return id;
  }, [clearTimer, startTimer]);

  // ── Listen for notification events ──
  useEffect(() => {
    const handler = (e) => addNotification(e.detail);
    window.addEventListener('vc-notification', handler);
    return () => window.removeEventListener('vc-notification', handler);
  }, [addNotification]);

  // ── Listen for SW forwarded push notifications ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Dedup: track recent tags to prevent duplicate toasts
    const recentPushTags = new Map();
    const DEDUP_WINDOW = 10_000; // 10s

    const handler = (event) => {
      if (event.data?.type === 'PUSH_NOTIFICATION') {
        const payload = event.data.payload;
        const notifType = payload.data?.type || 'general';
        const tag = payload.tag || `${notifType}-${Date.now()}`;

        // Dedup: skip if same tag seen recently
        const now = Date.now();
        for (const [k, ts] of recentPushTags) {
          if (now - ts > DEDUP_WINDOW) recentPushTags.delete(k);
        }
        if (recentPushTags.has(tag)) return;
        recentPushTags.set(tag, now);

        // WhatsApp page awareness: if user is on WhatsApp tab, play sound only
        const path = window.location.pathname;
        const hash = window.location.hash;
        const isOnWhatsApp =
          (path.includes('/whatsapp') && hash === '#inbox') ||
          path.includes('/whatsapp-inbox');
        if (notifType === 'whatsapp_message' && isOnWhatsApp) {
          try { new Audio('/sounds/wa-notify.mp3').play().catch(() => {}); } catch {}
          return;
        }

        addNotification({
          type: notifType,
          title: payload.title,
          message: payload.body,
          data: payload.data,
          batchId: payload.data?.batchId,
          tag,
        });
      }
      if (event.data?.type === 'NOTIFICATION_CLICKED') {
        trackNotification(event.data.payload?.tag || 'unknown', 'clicked');
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [addNotification]);

  // ── Navigation handler ──
  const handleAction = useCallback((item) => {
    dismissNotification(item.id);
    trackNotification(item.id, 'clicked', { type: item.type });

    const prefix = getRoutePrefix();
    const url = item.data?.url || item.link;

    if (url) {
      navigate(url);
      return;
    }

    // Fallback deep links by type
    switch (item.type) {
      case 'class_started':
        if (item.batchId) navigate(`${prefix}/classroom/${item.batchId}`);
        break;
      case 'chat_message':
        navigate(`${prefix}/chat`);
        break;
      case 'assignment_posted':
        if (item.batchId) navigate(`${prefix}/assignments`);
        break;
      default:
        break;
    }
  }, [dismissNotification, navigate]);

  const handleRemindLater = useCallback((item) => {
    dismissNotification(item.id);
    remindMeLater(item, 5 * 60 * 1000);
  }, [dismissNotification]);

  // ── Cleanup all timers on unmount ──
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  return (
    <>
      {/* ── Top Banner (class started, critical alerts) ── */}
      {banner && (
        <div className={`inapp-banner inapp-banner--${banner.type} ${banner.exiting ? 'inapp-banner--exit' : 'inapp-banner--enter'}`}>
          <div className="inapp-banner__content">
            <div className="inapp-banner__icon">
              {React.createElement(ICON_MAP[banner.type] || Bell, { size: 20 })}
            </div>
            <div className="inapp-banner__text">
              <span className="inapp-banner__title">{banner.title}</span>
              <span className="inapp-banner__message">{banner.message}</span>
            </div>
          </div>
          <div className="inapp-banner__actions">
            {CTA_MAP[banner.type] && (
              <button
                className="inapp-banner__cta"
                onClick={() => handleAction(banner)}
              >
                {React.createElement(CTA_MAP[banner.type].icon, { size: 15 })}
                <span>{CTA_MAP[banner.type].label}</span>
              </button>
            )}
            {banner.type === 'class_started' && (
              <button
                className="inapp-banner__remind"
                onClick={() => handleRemindLater(banner)}
              >
                <Clock size={14} />
                <span>Remind Later</span>
              </button>
            )}
            <button
              className="inapp-banner__close"
              onClick={() => dismissNotification(banner.id)}
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Toast stack (bottom right) ── */}
      {toasts.length > 0 && (
        <div className="inapp-toast-container">
          {toasts.map((toast) => {
            // ── WhatsApp-styled toast: chat-bubble look (light/dark themed) ──
            if (toast.type === 'whatsapp_message') {
              const phone = toast.data?.phone || '';
              const contactName = toast.data?.contactName || toast.title?.replace(/^WhatsApp:\s*/i, '') || phone || 'WhatsApp';
              const initials = (contactName || '?').trim().split(/\s+/).slice(0, 2)
                .map((p) => p[0]?.toUpperCase() || '').join('') || 'WA';
              const time = new Date(toast.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return (
                <div
                  key={toast.id}
                  className={`wa-toast ${toast.exiting ? 'wa-toast--exit' : 'wa-toast--enter'}`}
                  role="alert"
                >
                  <button
                    className="wa-toast__close"
                    onClick={() => dismissNotification(toast.id)}
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                  <div className="wa-toast__header">
                    <div className="wa-toast__avatar">{initials}</div>
                    <div className="wa-toast__meta">
                      <div className="wa-toast__name">{contactName}</div>
                      <div className="wa-toast__sub">
                        <MessageSquare size={11} />
                        <span>WhatsApp</span>
                        <span className="wa-toast__dot">•</span>
                        <span>{time}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="wa-toast__bubble"
                    onClick={() => handleAction(toast)}
                    aria-label="Open WhatsApp inbox"
                  >
                    <span className="wa-toast__bubble-text">{toast.message || 'New message'}</span>
                    <span className="wa-toast__bubble-tail" aria-hidden="true" />
                  </button>
                  <div className="wa-toast__cta-row">
                    <button
                      className="wa-toast__cta"
                      onClick={() => handleAction(toast)}
                    >
                      <Reply size={13} />
                      <span>Open Inbox</span>
                    </button>
                  </div>
                  <div
                    className="wa-toast__progress"
                    style={{ animationDuration: `${TOAST_DURATION}ms` }}
                  />
                </div>
              );
            }

            // ── Default toast layout ──
            const IconComp = ICON_MAP[toast.type] || Bell;
            const cta = CTA_MAP[toast.type];
            return (
              <div
                key={toast.id}
                className={`inapp-toast inapp-toast--${toast.type} ${toast.exiting ? 'inapp-toast--exit' : 'inapp-toast--enter'}`}
              >
                <button
                  className="inapp-toast__close"
                  onClick={() => dismissNotification(toast.id)}
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>
                <div className="inapp-toast__body">
                  <div className={`inapp-toast__icon inapp-toast__icon--${toast.type}`}>
                    <IconComp size={18} />
                  </div>
                  <div className="inapp-toast__content">
                    <div className="inapp-toast__title">{toast.title}</div>
                    <div className="inapp-toast__message">{toast.message}</div>
                  </div>
                </div>
                {cta && (
                  <div className="inapp-toast__actions">
                    <button
                      className="inapp-toast__cta"
                      onClick={() => handleAction(toast)}
                    >
                      {React.createElement(cta.icon, { size: 14 })}
                      <span>{cta.label}</span>
                    </button>
                    <button
                      className="inapp-toast__remind"
                      onClick={() => handleRemindLater(toast)}
                    >
                      <Clock size={13} />
                    </button>
                  </div>
                )}
                <div
                  className="inapp-toast__progress"
                  style={{ animationDuration: `${TOAST_DURATION}ms` }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal (critical/blocking) ── */}
      {modal && (
        <div className="inapp-modal-overlay" onClick={() => dismissNotification(modal.id)}>
          <div
            className={`inapp-modal inapp-modal--${modal.type}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="inapp-modal__close"
              onClick={() => dismissNotification(modal.id)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="inapp-modal__icon">
              {React.createElement(ICON_MAP[modal.type] || Bell, { size: 32 })}
            </div>
            <h3 className="inapp-modal__title">{modal.title}</h3>
            <p className="inapp-modal__message">{modal.message}</p>
            <div className="inapp-modal__actions">
              {CTA_MAP[modal.type] && (
                <button
                  className="inapp-modal__cta"
                  onClick={() => handleAction(modal)}
                >
                  {React.createElement(CTA_MAP[modal.type].icon, { size: 16 })}
                  <span>{CTA_MAP[modal.type].label}</span>
                </button>
              )}
              <button
                className="inapp-modal__dismiss"
                onClick={() => dismissNotification(modal.id)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InAppNotification;
